import React, { useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatNumberRu } from '@/core/helpers/PageHelpers';
import { CapitalGroup } from '@/core/helpers/charterCapital';

interface CharterCapitalDialogProps {
    open: boolean;
    onClose: () => void;
    groups: CapitalGroup[];
}

const tg = (value: number) => `${formatNumberRu(Math.round(value))} тг`;

const SHORTAGE_COLOR = "#c62828";
const OK_COLOR = "#2e7d32";

export const CharterCapitalDialog = ({ open, onClose, groups }: CharterCapitalDialogProps) => {
    const [showAll, setShowAll] = useState(false);
    const visibleGroups = showAll ? groups : groups.filter(group => group.shortage > 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                Уставной капитал
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Норма: 100 000 000 тг на точку в Алматы и Астане, 50 000 000 тг — в остальных регионах.
                        Капитал юрлица — остатки всех его точек в тенге по курсу НБ РК.
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
                        label="Все юрлица"
                    />
                </Box>

                {visibleGroups.length === 0 && (
                    <Typography sx={{ textAlign: "center", color: OK_COLOR, py: 2 }}>
                        Недостачи уставного капитала нет
                    </Typography>
                )}

                {visibleGroups.map(group => (
                    <Box key={group.key} sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: "1.05rem" }}>
                                {group.names.join(" + ")}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: group.shortage > 0 ? SHORTAGE_COLOR : OK_COLOR }}>
                                {group.shortage > 0 ? `Недостача ${tg(group.shortage)}` : "Достаточно"}
                            </Typography>
                        </Box>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Точка</TableCell>
                                        <TableCell>Регион</TableCell>
                                        <TableCell align="right">Норма</TableCell>
                                        <TableCell align="right">Баланс в тг</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {group.departments.map(({ department, balance, required }) => (
                                        <TableRow key={department.id}>
                                            <TableCell>{department.code}</TableCell>
                                            <TableCell>{department.region || department.city}</TableCell>
                                            <TableCell align="right">{tg(required)}</TableCell>
                                            <TableCell align="right">{tg(balance)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                                        <TableCell colSpan={2}>Итого по юрлицу</TableCell>
                                        <TableCell align="right">{tg(group.required)}</TableCell>
                                        <TableCell align="right" sx={{ color: group.shortage > 0 ? SHORTAGE_COLOR : OK_COLOR }}>
                                            {tg(group.balance)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};

export default CharterCapitalDialog;
